import React, { useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { FaSortUp, FaSortDown, FaSort, FaChevronRight, FaChevronDown } from 'react-icons/fa'
import { formatEur, computePricePositions } from './networkUtils'

const PROCESS_FAMILIES = [
  { key: 'all', label: 'All' },
  { key: 'machining', label: 'Machining' },
  { key: 'sheet_metal', label: 'Sheet Metal' },
  { key: 'other', label: 'Other' },
]

const MACHINING_AXES = ['material', 'size', 'tolerance_band', 'axis_tier', 'complexity_tier']
const SHEET_METAL_AXES = ['material', 'size', 'gauge_band', 'bend_class', 'complexity_tier']
const OTHER_AXES = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']
const ALL_AXES = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']

function getAvailableAxes(processFamily) {
  switch (processFamily) {
    case 'machining': return MACHINING_AXES
    case 'sheet_metal': return SHEET_METAL_AXES
    case 'other': return OTHER_AXES
    default: return ALL_AXES
  }
}

// Logical sort orders for non-alphabetical axes
const LOGICAL_ORDERS = {
  size: ['S', 'M', 'L', 'XL', 'XXL', 'unknown'],
  tolerance_band: ['ultra', 'precision', 'standard', 'commercial', 'tol_unspec', 'unknown'],
  complexity_tier: ['simple', 'moderate', 'complex', 'complexity_unspec', 'unknown'],
  axis_tier: ['2axis', '3axis', '4axis', '5axis', '5axis_plus', 'axis_unspec', 'unknown'],
  gauge_band: ['thin', 'medium', 'heavy', 'plate', 'gauge_unspec', 'unknown'],
  bend_class: ['flat', 'simple', 'moderate', 'complex', 'bends_unspec', 'unknown'],
}

function getAxisSortRank(axis, value) {
  const order = LOGICAL_ORDERS[axis]
  if (!order) return null // alphabetical
  const idx = order.indexOf(value)
  return idx >= 0 ? idx : order.length // unknown values sort last
}

export default function FamiliesView({ parts, families }) {
  const [processFamily, setProcessFamily] = useState('all')
  const [activeAxes, setActiveAxes] = useState(['material', 'size'])
  const [sortCol, setSortCol] = useState('family')
  const [sortDir, setSortDir] = useState('asc')
  const [expandedRows, setExpandedRows] = useState(new Set())

  const availableAxes = getAvailableAxes(processFamily)

  // Enrich parts with price positions
  const enrichedParts = useMemo(() => computePricePositions(parts), [parts])

  // Filter parts by process family
  const filteredParts = useMemo(() => {
    if (processFamily === 'all') return enrichedParts
    return enrichedParts.filter(p => p.process_family === processFamily)
  }, [enrichedParts, processFamily])

  // Build dynamic families based on active axes (skip unknown values)
  const isUnknown = (val) => {
    if (!val) return true
    const lower = (val.toLowerCase ? val.toLowerCase() : String(val)).trim()
    if (!lower) return true
    if (lower === 'unknown' || lower === 'unassigned' || lower === 'n/a' || lower === 'none') return true
    if (lower.endsWith('_unspec') || lower.endsWith('unspec')) return true
    return false
  }

  const dynamicFamilies = useMemo(() => {
    const groups = {}
    filteredParts.forEach(part => {
      // Only include axes where value is known
      const knownAxes = activeAxes.filter(ax => {
        const val = part[ax]
        return !isUnknown(val)
      })
      if (knownAxes.length === 0) return // skip parts with no known attributes for selected axes
      const keyParts = knownAxes.map(ax => part[ax])
      const key = keyParts.join('__')
      if (!groups[key]) {
        groups[key] = {
          key,
          label: keyParts.map(k => k.replace(/_/g, ' ')).join(' · '),
          axes: Object.fromEntries(knownAxes.map((ax, i) => [ax, keyParts[i]])),
          parts: [],
          suppliers: new Set(),
          spend: 0,
        }
      }
      groups[key].parts.push(part)
      groups[key].spend += part.total_spend || 0
      part.suppliers.forEach(s => groups[key].suppliers.add(s.name))
    })

    return Object.values(groups).map(g => ({
      ...g,
      partsCount: g.parts.length,
      suppliersCount: g.suppliers.size,
    }))
  }, [filteredParts, activeAxes])

  // Sort
  const sorted = useMemo(() => {
    return [...dynamicFamilies].sort((a, b) => {
      if (sortCol === 'family') {
        // Sort by each axis in order, using logical ordering where applicable
        for (const ax of activeAxes) {
          const av = a.axes[ax] || '\uffff'
          const bv = b.axes[ax] || '\uffff'
          if (av === bv) continue
          const rankA = getAxisSortRank(ax, av)
          const rankB = getAxisSortRank(ax, bv)
          let cmp
          if (rankA !== null && rankB !== null) {
            cmp = rankA - rankB
          } else {
            cmp = String(av).localeCompare(String(bv))
          }
          if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp
        }
        return 0
      }
      let av, bv
      switch (sortCol) {
        case 'parts': av = a.partsCount; bv = b.partsCount; break
        case 'suppliers': av = a.suppliersCount; bv = b.suppliersCount; break
        case 'spend': av = a.spend; bv = b.spend; break
        default: av = a.label; bv = b.label; break
      }
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [dynamicFamilies, sortCol, sortDir, activeAxes])

  const handleSort = (col) => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir(col === 'family' ? 'asc' : 'desc') }
  }

  const toggleExpand = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleAxis = (ax) => {
    setActiveAxes(prev =>
      prev.includes(ax) ? prev.filter(a => a !== ax) : [...prev, ax]
    )
    setExpandedRows(new Set())
  }

  return (
    <div className="space-y-4">
      {/* Process family selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase">Process:</span>
        {PROCESS_FAMILIES.map(pf => (
          <button
            key={pf.key}
            onClick={() => { setProcessFamily(pf.key); setActiveAxes(getAvailableAxes(pf.key).slice(0, 2)); setExpandedRows(new Set()) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              processFamily === pf.key
                ? 'bg-[#1e5045] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {pf.label}
            {pf.key !== 'all' && (
              <span className="ml-1 opacity-70">
                ({parts.filter(p => pf.key === 'all' || p.process_family === pf.key).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Axis toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 uppercase">Group by:</span>
        {availableAxes.map(ax => (
          <button
            key={ax}
            onClick={() => toggleAxis(ax)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              activeAxes.includes(ax)
                ? 'bg-[#1e5045]/10 border-[#1e5045] text-[#1e5045]'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'
            }`}
          >
            {ax.replace(/_/g, ' ').replace('band', '').replace('tier', '').trim()}
          </button>
        ))}
      </div>

      {/* Families table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {sorted.length} Part Families
          </span>
          <span className="text-xs text-gray-400">
            {filteredParts.length} parts total
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="w-8 px-2"></th>
                <SortThLeft col="family" label="Family" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="parts" label="Parts" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="suppliers" label="Suppliers" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="spend" label="Spend" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(fam => (
                <React.Fragment key={fam.key}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer group">
                    <td className="px-2" onClick={(e) => { e.stopPropagation(); toggleExpand(fam.key) }}>
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] border transition-colors ${
                        expandedRows.has(fam.key) ? 'bg-[#1e5045] text-white border-[#1e5045]' : 'bg-gray-50 text-gray-400 border-gray-200 group-hover:border-gray-400'
                      }`}>
                        {expandedRows.has(fam.key) ? <FaChevronDown size={8} /> : <FaChevronRight size={8} />}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-800 capitalize">{fam.label}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fam.partsCount}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`font-medium ${fam.suppliersCount >= 5 ? 'text-red-600' : fam.suppliersCount >= 3 ? 'text-amber-600' : 'text-green-600'}`}>
                        {fam.suppliersCount}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-[#1e5045]">{formatEur(fam.spend)}</td>
                  </tr>
                  {expandedRows.has(fam.key) && (
                    <tr>
                      <td colSpan={5} className="p-0">
                        <ExpandedFamilyRow parts={fam.parts} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function buildPPClipboardTextFam(s, part) {
  const pp = s.price_position
  if (pp == null || !s.benchmark) return ''
  const pct = Math.abs(Math.round((pp - 1) * 100))
  const direction = pp > 1 ? 'above' : 'below'
  const eau = parseInt(s.eau) || 0
  const verdict = pp > 1.5 ? 'Above expected — negotiation target' : pp < 0.7 ? 'Well below expected — good deal' : 'Within normal range'
  let header = ''
  if (part) {
    header = `Part: ${part.pn || '—'}${part.description ? ' — ' + part.description : ''}\n`
  }
  let formula = ''
  if (s.cohort_median_uc && s.cohort_median_eau && eau > 0) {
    formula = `\nFormula: Expected = Cohort Median UC × (Cohort Median EAU / Part EAU)^0.4\n       = €${s.cohort_median_uc.toFixed(2)} × (${s.cohort_median_eau.toLocaleString()} / ${eau.toLocaleString()})^0.4\n       = €${s.benchmark.toFixed(2)}`
  }
  let text = `${header}Supplier: ${s.name}\nActual: €${Number(s.unit_cost).toFixed(2)}\nExpected: €${s.benchmark.toFixed(2)}${eau > 0 ? ` (EAU ${eau.toLocaleString()})` : ''}\nPosition: ${pct}% ${direction} benchmark\n${verdict}${formula}`
  const entries = (s.cohort_entries || []).sort((a, b) => b.unit_cost - a.unit_cost)
  if (entries.length > 0) {
    const cohortLabel = (s.cohort_name || 'Unknown').replace(/_/g, ' ').replace(/\|/g, ', ')
    text += `\n\nCohort: ${cohortLabel} (${s.cohort_size || entries.length} transactions from parts with same process family/method/material/size)\nMedian UC: €${s.cohort_median_uc?.toFixed(2) || '?'}, Median EAU: ${s.cohort_median_eau?.toLocaleString() || '?'}`
    text += `\nPart #\tDescription\tSupplier\tUC\tEAU`
    entries.forEach(e => {
      text += `\n${e.pn || '—'}\t${e.description || '—'}\t${e.supplier}\t€${e.unit_cost.toFixed(2)}\t${e.eau > 0 ? Math.round(e.eau).toLocaleString() : '—'}`
    })
  }
  return text
}

function PPTooltipFam({ s, part }) {
  const pp = s.price_position
  if (pp == null || !s.benchmark) return null

  const pct = Math.abs(Math.round((pp - 1) * 100))
  const direction = pp > 1 ? 'above' : 'below'
  const eau = parseInt(s.eau) || 0
  const verdictColor = pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-700' : 'text-gray-600'
  const verdict = pp > 1.5 ? '⚠ Above expected — negotiation target' : pp < 0.7 ? '✓ Well below expected — good deal' : '○ Within normal range'

  const entries = (s.cohort_entries || []).sort((a, b) => b.unit_cost - a.unit_cost).slice(0, 10)
  const hasFormula = s.cohort_median_uc && s.cohort_median_eau && eau > 0
  const cohortLabel = (s.cohort_name || 'Unknown').replace(/_/g, ' ').replace(/\|/g, ', ')

  return (
    <div>
      {part && (
        <div className="text-[10px] text-gray-500 mb-1 truncate">
          <span className="font-mono font-medium text-gray-700">{part.pn}</span>
          {part.description && <span className="ml-1.5">{part.description}</span>}
        </div>
      )}
      <div className={`flex ${entries.length > 0 ? 'gap-4' : ''}`}>
        {/* Left: Price position details */}
        <div className="shrink-0">
          <div className="font-semibold text-[11px] text-gray-900 truncate mb-1">{s.name}</div>
          <table className="text-[10px]">
            <tbody>
              <tr><td className="text-gray-500 pr-2 py-0.5">Actual</td><td className="font-mono text-gray-900">€{Number(s.unit_cost).toFixed(2)}</td></tr>
              <tr><td className="text-gray-500 pr-2 py-0.5">Expected</td><td className="font-mono text-gray-900">€{s.benchmark.toFixed(2)}{eau > 0 ? <span className="text-gray-400 ml-1">(EAU {eau.toLocaleString()})</span> : ''}</td></tr>
              <tr><td className="text-gray-500 pr-2 py-0.5">Position</td><td className={`font-semibold ${verdictColor}`}>{pct}% {direction}</td></tr>
            </tbody>
          </table>
          <div className={`text-[10px] font-medium mt-1 ${verdictColor}`}>{verdict}</div>
          {hasFormula && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-100 text-[9px] text-gray-500 font-mono leading-relaxed">
              <div>Expected = Median UC × (Median EAU / Part EAU)<sup>0.4</sup></div>
              <div className="text-gray-400">= €{s.cohort_median_uc.toFixed(2)} × ({s.cohort_median_eau.toLocaleString()} / {eau.toLocaleString()})<sup>0.4</sup> = €{s.benchmark.toFixed(2)}</div>
            </div>
          )}
        </div>
        {/* Right: Cohort table */}
        {entries.length > 0 && (
          <div className="border-l border-gray-200 pl-3 min-w-0">
            <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {cohortLabel} ({s.cohort_size || entries.length})
              {s.cohort_median_uc && <span className="font-normal normal-case ml-1">· median €{s.cohort_median_uc.toFixed(2)}</span>}
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left font-medium pb-0.5 pr-2">Part</th>
                  <th className="text-left font-medium pb-0.5 pr-2">Supplier</th>
                  <th className="text-right font-medium pb-0.5 pr-2">UC</th>
                  <th className="text-right font-medium pb-0.5">EAU</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className={e.supplier === s.name ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                    <td className="pr-2 py-0.5 truncate max-w-[140px] font-mono" title={`${e.pn} — ${e.description || ''}`}>{e.pn?.slice(0, 16) || '—'}{e.pn?.length > 16 ? '…' : ''}</td>
                    <td className="pr-2 py-0.5 truncate max-w-[140px]">{e.supplier.slice(0, 20)}{e.supplier.length > 20 ? '…' : ''}</td>
                    <td className="text-right pr-2 py-0.5 font-mono">€{e.unit_cost.toFixed(2)}</td>
                    <td className="text-right py-0.5 font-mono">{e.eau > 0 ? Math.round(e.eau).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(s.cohort_entries || []).length > 10 && (
              <div className="text-[9px] text-gray-400 mt-0.5">+{s.cohort_entries.length - 10} more</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function PPSupplierChip({ s, multi, part }) {
  const [copied, setCopied] = React.useState(false)
  const [showTip, setShowTip] = React.useState(false)
  const [tipStyle, setTipStyle] = React.useState({})
  const chipRef = React.useRef(null)
  const tipRef = React.useRef(null)
  const hideTimer = React.useRef(null)
  const pp = s.price_position
  const ppColor = pp == null ? '' : pp > 1.5 ? 'text-red-600' : pp < 0.7 ? 'text-green-600' : ''
  const uc = s.unit_cost > 0 ? formatEur(s.unit_cost) : null
  const hasTip = pp != null && s.benchmark

  const positionTip = () => {
    if (!chipRef.current) return
    const rect = chipRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < 350
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - 250, window.innerWidth - 520))
    if (above) {
      setTipStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 8, left, zIndex: 9999 })
    } else {
      setTipStyle({ position: 'fixed', top: rect.bottom + 8, left, zIndex: 9999 })
    }
  }

  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null } }
  const scheduleHide = () => { cancelHide(); hideTimer.current = setTimeout(() => setShowTip(false), 150) }

  const handleMouseEnter = () => {
    cancelHide()
    positionTip()
    setShowTip(true)
  }

  const handleMouseLeave = () => { scheduleHide() }
  const handleTipEnter = () => { cancelHide() }
  const handleTipLeave = () => { scheduleHide() }

  const handleCopy = (e) => {
    e.stopPropagation()
    const text = buildPPClipboardTextFam(s, part)
    if (text) {
      navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  return (
    <span ref={chipRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] relative ${
      multi ? 'bg-amber-100 text-amber-800' : 'bg-[#1e5045]/10 text-[#1e5045]'
    }`}>
      <span>{s.name.length > 20 ? s.name.slice(0, 20) + '…' : s.name}</span>
      {uc && <span className="opacity-70 ml-0.5">{uc}</span>}
      {pp != null && (
        <span className={`font-semibold ml-0.5 cursor-help ${ppColor}`}>
          {pp.toFixed(2)}{pp > 1.5 ? ' ⚠️' : pp < 0.7 ? ' ✓' : ''}
        </span>
      )}
      {hasTip && showTip && ReactDOM.createPortal(
        <div
          ref={tipRef}
          onClick={handleCopy}
          onMouseEnter={handleTipEnter}
          onMouseLeave={handleTipLeave}
          style={tipStyle}
          className="min-w-[420px] max-w-[680px] w-max bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:border-gray-300 transition-colors"
        >
          <PPTooltipFam s={s} part={part} />
          <div className="mt-2 pt-1.5 border-t border-gray-100 text-[9px] text-gray-400 text-center">
            {copied ? '✓ Copied to clipboard' : 'Click to copy'}
          </div>
        </div>,
        document.body
      )}
    </span>
  )
}

function ExpandedFamilyRow({ parts }) {
  const sorted = [...parts].sort((a, b) => (b.total_spend || 0) - (a.total_spend || 0))
  return (
    <div className="mx-4 mb-3 ml-9 border-l-3 border-[#1e5045] rounded-r-lg bg-gray-50 overflow-hidden">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Part</th>
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Tolerance</th>
            <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Suppliers</th>
            <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase text-[9px]">Spend</th>
          </tr>
        </thead>
        <tbody>
          {sorted.slice(0, 20).map(part => (
            <tr key={part.pn} className="border-b border-gray-100 last:border-0 hover:bg-white/50">
              <td className="px-3 py-1.5">
                <div className="font-mono font-medium text-gray-700">{part.pn}</div>
                {part.description && <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{part.description}</div>}
              </td>
              <td className="px-3 py-1.5 text-gray-500">
                {part.tightest_mm ? `±${part.tightest_mm.toFixed(3)}mm` : '—'}
                {part.tolerance_outlier && <span className="ml-1 text-red-500 text-[9px]">⚠ outlier</span>}
              </td>
              <td className="px-3 py-1.5">
                <div className="flex flex-wrap gap-1">
                  {part.suppliers.map(s => (
                    <PPSupplierChip key={s.name} s={s} multi={part.suppliers.length > 1} part={part} />
                  ))}
                </div>
              </td>
              <td className="px-3 py-1.5 text-right font-medium text-[#1e5045]">{formatEur(part.total_spend)}</td>
            </tr>
          ))}
          {parts.length > 20 && (
            <tr><td colSpan={4} className="px-3 py-1.5 text-center text-gray-400 text-[10px]">
              + {parts.length - 20} more parts
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function SortThLeft({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col
  return (
    <th
      className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
      onClick={() => onSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active
          ? sortDir === 'asc' ? <FaSortUp size={10} className="text-[#1e5045]" /> : <FaSortDown size={10} className="text-[#1e5045]" />
          : <FaSort size={10} className="text-gray-300" />
        }
      </span>
    </th>
  )
}

function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col
  return (
    <th
      className="px-3 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
      onClick={() => onSort(col)}
    >
      <span className="flex items-center justify-end gap-1">
        {label}
        {active
          ? sortDir === 'asc' ? <FaSortUp size={10} className="text-[#1e5045]" /> : <FaSortDown size={10} className="text-[#1e5045]" />
          : <FaSort size={10} className="text-gray-300" />
        }
      </span>
    </th>
  )
}
